import { useState } from "react";
import PropTypes from "prop-types";
import { Icon } from "@iconify/react";
import {
  NavLink as RouterLink,
  matchPath,
  useLocation,
} from "react-router-dom";
import arrowIosForwardFill from "@iconify/icons-eva/arrow-ios-forward-fill";
import arrowIosDownwardFill from "@iconify/icons-eva/arrow-ios-downward-fill";
// material
import { alpha, styled } from "@mui/material/styles";
import {
  Box,
  List,
  Collapse,
  ListItemText,
  ListItemIcon,
  ListItemButton,
  Divider,
  Tooltip,
} from "@mui/material";

// ----------------------------------------------------------------------

const ListItemStyle = styled((props) => (
  <ListItemButton disableGutters {...props} />
))(({ theme, isCollapsed }) => ({
  ...theme.typography.body2,
  height: 48,
  position: "relative",
  textTransform: "capitalize",
  paddingLeft: isCollapsed ? theme.spacing(0.5) : theme.spacing(2),
  paddingRight: isCollapsed ? theme.spacing(0.5) : theme.spacing(2.5),
  color: theme.palette.text.secondary,
  width: "100%",
  justifyContent: isCollapsed ? "center" : "flex-start",
  minWidth: isCollapsed ? "40px" : "auto",
  borderRadius: "4px",
  margin: isCollapsed ? "2px 0" : "0",
  "&:hover": {
    background: isCollapsed
      ? alpha(theme.palette.primary.main, 0.12)
      : theme.palette.gradients.brandSoft,
    borderRadius: "4px",
  },
  "&:before": {
    top: 0,
    left: 0,
    width: 3,
    bottom: 0,
    content: "''",
    display: "none",
    position: "absolute",
    borderTopLeftRadius: 4,
    borderBottomLeftRadius: 4,
    backgroundColor: theme.palette.primary.main,
  },
}));

const ListItemIconStyle = styled(ListItemIcon)(({ theme, svgColor }) => ({
  width: 22,
  height: 22,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  color:
    svgColor === "active"
      ? theme.palette.primary.main
      : theme.palette.text.secondary,
  transition: theme.transitions.create("color", {
    duration: theme.transitions.duration.shortest,
  }),
  "& svg": {
    color: "inherit",
  },
}));

// ----------------------------------------------------------------------

NavItem.propTypes = {
  item: PropTypes.object,
  active: PropTypes.func,
  isCollapsed: PropTypes.bool,
};

function NavItem({ item, active, isCollapsed }) {
  const isActiveRoot = active(item.path);
  const { title, path, icon, info, children } = item;
  const [open, setOpen] = useState(isActiveRoot && !isCollapsed);

  const handleOpen = () => {
    setOpen((prev) => !prev);
  };

  const activeRootStyle = {
    color: "primary.main",
    fontWeight: "fontWeightMedium",
    bgcolor: "transparent",
    background: (theme) => theme.palette.gradients.brandSoft,
    "&:before": {
      display: "block",
    },
  };

  const activeSubStyle = {
    color: "text.primary",
    fontWeight: "fontWeightMedium",
  };

  if (children) {
    return (
      <>
        <Tooltip 
          title={isCollapsed ? title : ""} 
          placement="right" 
          arrow
        >
          <ListItemStyle
            onClick={handleOpen}
            isCollapsed={isCollapsed}
            sx={{
              ...(isActiveRoot && activeRootStyle),
            }}
          >
            <ListItemIconStyle svgColor={isActiveRoot ? "active" : "default"}>
              {icon && icon}
            </ListItemIconStyle>
            {!isCollapsed && (
              <>
                <ListItemText disableTypography primary={title} />
                {info && info}
                <Box
                  component={Icon}
                  icon={open ? arrowIosDownwardFill : arrowIosForwardFill}
                  sx={{ width: 16, height: 16, ml: 1 }}
                />
              </>
            )}
          </ListItemStyle>
        </Tooltip>

        {/* Divider directly below the gradient */}
        {!isCollapsed && (
          <Divider
            sx={(theme) => ({
              borderColor: alpha(
                theme.palette.divider,
                theme.palette.mode === "light" ? 0.2 : 0.5
              ),
              margin: 0,
              width: "100%",
            })}
          />
        )}

        {!isCollapsed && (
          <Collapse in={open} timeout="auto" unmountOnExit>
            <List component="div" disablePadding>
              {children.map((item) => {
                const { title, path } = item;
                const isActiveSub = active(path);

                return (
                  <>
                    <ListItemStyle
                      key={title}
                      component={RouterLink}
                      to={path}
                      isCollapsed={isCollapsed}
                      sx={{
                        ...(isActiveSub && activeSubStyle),
                      }}
                    >
                      <ListItemIconStyle>
                        <Box
                          component="span"
                          sx={{
                            width: 4,
                            height: 4,
                            display: "flex",
                            borderRadius: "50%",
                            alignItems: "center",
                            justifyContent: "center",
                            bgcolor: "text.disabled",
                            transition: (theme) =>
                              theme.transitions.create("transform"),
                            ...(isActiveSub && {
                              transform: "scale(2)",
                              bgcolor: "primary.main",
                            }),
                          }}
                        />
                      </ListItemIconStyle>
                      <ListItemText disableTypography primary={title} />
                    </ListItemStyle>

                    {/* Add Divider */}
                    <Divider
                      sx={(theme) => ({
                        borderColor: alpha(
                          theme.palette.divider,
                          theme.palette.mode === "light" ? 0.2 : 0.5
                        ),
                        my: 1,
                      })}
                    />
                  </>
                );
              })}
            </List>
          </Collapse>
        )}
      </>
    );
  }

  return (
    <>
      <Tooltip 
        title={isCollapsed ? title : ""} 
        placement="right" 
        arrow
      >
        <ListItemStyle
          component={RouterLink}
          to={path}
          isCollapsed={isCollapsed}
          sx={{
            ...(isActiveRoot && activeRootStyle),
          }}
        >
          <ListItemIconStyle svgColor={isActiveRoot ? "active" : "default"}>
            {icon && icon}
          </ListItemIconStyle>
          {!isCollapsed && (
            <>
              <ListItemText disableTypography primary={title} />
              {info && info}
            </>
          )}
        </ListItemStyle>
      </Tooltip>

      {/* Add Divider */}
      {!isCollapsed && (
        <Divider
          sx={(theme) => ({
            borderColor: alpha(
              theme.palette.divider,
              theme.palette.mode === "light" ? 0.2 : 0.5
            ),
            margin: 0,
          })}
        />
      )}
    </>
  );
}

NavSection.propTypes = {
  navConfig: PropTypes.array,
  isCollapsed: PropTypes.bool,
};

export default function NavSection({ navConfig, isCollapsed, ...other }) {
  const { pathname } = useLocation();
  const match = (path) =>
    path ? !!matchPath({ path, end: false }, pathname) : false;

  return (
    <Box
      sx={{
        mx: isCollapsed ? 0.5 : 1.3,
        borderRadius: "6px",
        width: isCollapsed ? "100px" : "236px",
        backgroundColor: (theme) =>
          alpha(
            theme.palette.mode === "light"
              ? theme.palette.background.paper
              : theme.palette.background.default,
            theme.palette.mode === "light" ? 0.96 : 0.72
          ),
        border: (theme) =>
          `1px solid ${alpha(
            theme.palette.divider,
            theme.palette.mode === "light" ? 0.2 : 0.5
          )}`,
        boxShadow: (theme) =>
          theme.palette.mode === "light"
            ? "0px 6px 18px rgba(15, 23, 42, 0.08)"
            : "0px 14px 30px rgba(0, 0, 0, 0.65)",
        padding: isCollapsed ? "8px 4px" : 2,
        transition: "all 0.3s ease",
        display: "flex",
        flexDirection: "column",
        alignItems: isCollapsed ? "center" : "stretch",
      }}
    >
      <Box {...other}>
        <List disablePadding>
          {navConfig?.map((item) => (
            <NavItem key={item.title} item={item} active={match} isCollapsed={isCollapsed} />
          ))}
        </List>
      </Box>
    </Box>
  );
}